/**
 * Lógica pura da aba "Destinos" do modal do canal: o formulário do destino do
 * Instagram (zod, conversão de/para a API, diferenças para o "antes:") e as
 * regras da fila de posts (rótulos, quando cancelar e tentar de novo). Fica
 * fora dos componentes para ser testada sem DOM.
 *
 * Regras do backend espelhadas aqui: ``services/destinations/config.py`` (os
 * limites) e ``services/destinations/actions.py`` (cancelar e tentar de novo).
 * Ver apps/backend/docs/plan-platform-destinations.md § "Configurar no scheduler".
 */
import { z } from 'zod'

import type { ChipVariant } from '@/components/ui/Chip'
import type {
  DestinationPost,
  DestinationPostsPage,
  DestinationPostStatus,
  InstagramDestination,
  InstagramDestinationPatch,
  InstagramOptions,
} from '@/api/endpoints/destinations'
import type { Change, FieldSpec } from './model'

/** O fuso do ciclo de upload quando a config do scheduler não chegou. */
export const DEFAULT_UPLOAD_TZ = 'America/Sao_Paulo'
export const MAX_OFFSET_MINUTES = 720
export const MAX_PER_DAY_LIMIT = 10
export const MAX_PAUSE_WINDOWS = 20
export const MAX_CTA_CHARS = 150
export const MAX_HASHTAGS = 5

/** ``YYYY-MM-DDTHH:MM`` — o valor de um ``<input type="datetime-local">``. */
const LOCAL_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/

// ---------------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------------

/** Uma pausa, com as pontas no fuso do upload (sem fuso na string). */
export interface PauseRange {
  from: string
  until: string
}

export const destinationSchema = z
  .object({
    enabled: z.boolean(),
    timing_mode: z.enum(['with_primary', 'offset']),
    // Campo vazio vira NaN; só vale no modo que o mostra (checado abaixo).
    offset_minutes: z.union([z.number(), z.nan()]),
    max_mode: z.enum(['all', 'limit']),
    max_per_day: z.union([z.number(), z.nan()]),
    require_safety_approved: z.boolean(),
    caption_cta: z.string().max(MAX_CTA_CHARS, `Até ${String(MAX_CTA_CHARS)} caracteres`),
    include_source_credit: z.boolean(),
    hashtags_max: z
      .number({ error: 'De 0 a 5' })
      .int('Use um número inteiro')
      .min(0, 'De 0 a 5')
      .max(MAX_HASHTAGS, 'De 0 a 5'),
    share_to_feed: z.boolean(),
    trial: z.boolean(),
    pause_windows: z
      .array(z.object({ from: z.string(), until: z.string() }))
      .max(MAX_PAUSE_WINDOWS, `Até ${String(MAX_PAUSE_WINDOWS)} pausas`),
  })
  .superRefine((v, ctx) => {
    // Os dois números só valem no modo que os usa (o outro fica escondido).
    if (
      v.timing_mode === 'offset' &&
      (!Number.isInteger(v.offset_minutes) || v.offset_minutes < 0 || v.offset_minutes > MAX_OFFSET_MINUTES)
    ) {
      ctx.addIssue({ code: 'custom', path: ['offset_minutes'], message: 'De 0 a 720 minutos' })
    }
    if (
      v.max_mode === 'limit' &&
      (!Number.isInteger(v.max_per_day) || v.max_per_day < 1 || v.max_per_day > MAX_PER_DAY_LIMIT)
    ) {
      ctx.addIssue({ code: 'custom', path: ['max_per_day'], message: 'De 1 a 10' })
    }
    v.pause_windows.forEach((w, i) => {
      if (!LOCAL_DATETIME.test(w.from) || !LOCAL_DATETIME.test(w.until)) {
        ctx.addIssue({ code: 'custom', path: ['pause_windows', i], message: 'Preencha o início e o fim da pausa.' })
      } else if (w.until <= w.from) {
        ctx.addIssue({ code: 'custom', path: ['pause_windows', i], message: 'A pausa precisa terminar depois de começar.' })
      }
    })
  })

export type DestinationValues = z.infer<typeof destinationSchema>

/** Os erros do formulário, um por campo (``pause_windows.2`` para a 3ª pausa). */
export function destinationErrors(values: DestinationValues): Record<string, string> {
  const result = destinationSchema.safeParse(values)
  const out: Record<string, string> = {}
  if (result.success) return out
  for (const issue of result.error.issues) {
    const key = issue.path.map(String).join('.')
    if (key && !(key in out)) out[key] = issue.message
  }
  return out
}

// ---------------------------------------------------------------------------
// Fuso: a API grava as pausas em UTC; a tela edita no fuso do upload
// ---------------------------------------------------------------------------

function formatter(tz: string): Intl.DateTimeFormat {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }
  try {
    return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: tz })
  } catch {
    return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: DEFAULT_UPLOAD_TZ })
  }
}

/** ``2026-10-22T03:00:00+00:00`` → ``2026-10-22T00:00`` em ``America/Sao_Paulo``. */
export function toLocalInput(iso: string, tz: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const parts = formatter(tz).formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

/** ``2026-10-22T00:00`` → ``22/10 00:00`` — para o "antes:" e os resumos. */
export function localInputLabel(value: string): string {
  if (!LOCAL_DATETIME.test(value)) return value || '—'
  const [date = '', time = ''] = value.split('T')
  const [, month = '', day = ''] = date.split('-')
  return `${day}/${month} ${time}`
}

// ---------------------------------------------------------------------------
// De/para a API
// ---------------------------------------------------------------------------

export function toDestinationValues(dest: InstagramDestination, tz: string): DestinationValues {
  return {
    enabled: dest.enabled,
    timing_mode: dest.timing_mode,
    offset_minutes: dest.offset_minutes,
    max_mode: dest.max_per_day === null ? 'all' : 'limit',
    max_per_day: dest.max_per_day ?? 1,
    require_safety_approved: dest.require_safety_approved,
    caption_cta: dest.options.caption_cta,
    include_source_credit: dest.options.include_source_credit,
    hashtags_max: dest.options.hashtags_max,
    share_to_feed: dest.options.share_to_feed,
    trial: dest.options.trial,
    pause_windows: dest.pause_windows.map((w) => ({ from: toLocalInput(w.from, tz), until: toLocalInput(w.until, tz) })),
  }
}

function validOffset(minutes: number): number {
  return Number.isInteger(minutes) && minutes >= 0 && minutes <= MAX_OFFSET_MINUTES ? minutes : 0
}

/** O teto por dia como a API o grava: ``null`` = todos os do YouTube. */
function effectiveMax(v: DestinationValues): number | null {
  return v.max_mode === 'limit' ? v.max_per_day : null
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** Os dois retratos do formulário são iguais (NaN de campo vazio conta como igual). */
export function sameDestinationValues(a: DestinationValues, b: DestinationValues): boolean {
  return same(a, b)
}

const OPTION_KEYS = ['caption_cta', 'include_source_credit', 'hashtags_max', 'share_to_feed', 'trial'] as const

/**
 * O corpo do PUT: **só o que o dono mudou** desde que o formulário carregou
 * (``initial``), ou ``null`` quando não há o que gravar. Campo ausente mantém
 * o gravado (#309), então um formulário aberto antes do disjuntor não religa o
 * destino — ``enabled`` só vai quando o toggle mudou. ``max_per_day: null`` é
 * um valor ("todos"), e vai quando o dono escolheu "Todos os do YouTube".
 *
 * As pausas vão inteiras quando a lista mudou, e sem fuso: o backend as lê no
 * fuso do ciclo de upload, o mesmo em que a tela as mostra.
 */
export function toDestinationPatch(
  initial: DestinationValues,
  current: DestinationValues,
): InstagramDestinationPatch | null {
  const patch: InstagramDestinationPatch = { platform: 'instagram' }
  if (current.enabled !== initial.enabled) patch.enabled = current.enabled
  if (current.timing_mode !== initial.timing_mode) patch.timing_mode = current.timing_mode
  if (current.timing_mode === 'offset' && !Object.is(current.offset_minutes, initial.offset_minutes)) {
    patch.offset_minutes = validOffset(current.offset_minutes)
  }
  const max = effectiveMax(current)
  if (!Object.is(max, effectiveMax(initial))) patch.max_per_day = max
  if (current.require_safety_approved !== initial.require_safety_approved) {
    patch.require_safety_approved = current.require_safety_approved
  }
  if (!same(current.pause_windows, initial.pause_windows)) {
    patch.pause_windows = current.pause_windows.map((w) => ({ from: `${w.from}:00`, until: `${w.until}:00` }))
  }
  const options: Partial<InstagramOptions> = {}
  if (current.caption_cta.trim() !== initial.caption_cta.trim()) options.caption_cta = current.caption_cta.trim()
  for (const key of OPTION_KEYS) {
    if (key === 'caption_cta' || current[key] === initial[key]) continue
    Object.assign(options, { [key]: current[key] })
  }
  if (Object.keys(options).length) patch.options = options
  return Object.keys(patch).length > 1 ? patch : null
}

export interface Rebased {
  /** O novo "salvo": o que o servidor tem agora. */
  initial: DestinationValues
  /** O formulário: o que o dono editou fica; o resto passa ao valor do servidor. */
  current: DestinationValues
  /** Havia edição não salva quando o servidor mudou (vale avisar). */
  hadEdits: boolean
  /** Campos que o dono editou e que também mudaram no servidor. */
  conflicts: Array<keyof DestinationValues>
}

/**
 * Os campos que andam juntos: o número só vale no modo que o mostra. Na mescla
 * eles são uma unidade — misturar o modo do servidor com o número do dono
 * esconderia a edição (o número fica atrás do modo, e o PUT não o manda).
 */
const COUPLED: ReadonlyArray<ReadonlyArray<keyof DestinationValues>> = [
  ['timing_mode', 'offset_minutes'],
  ['max_mode', 'max_per_day'],
]

function mergeUnits(): Array<ReadonlyArray<keyof DestinationValues>> {
  const coupled = new Set(COUPLED.flat())
  const singles = (Object.keys(DESTINATION_FIELDS) as Array<keyof DestinationValues>).filter((k) => !coupled.has(k))
  return [...COUPLED, ...singles.map((k) => [k])]
}

/**
 * O servidor mudou depois que o formulário abriu (o disjuntor desligou o
 * destino, outra aba salvou): o formulário vai para o valor novo em tudo o que
 * o dono não editou, e mantém o que ele editou. Assim o toggle mostra a
 * verdade, e o PUT parcial seguinte só leva as edições do dono.
 *
 * Modo e número são uma unidade: se o dono mexeu em qualquer um dos dois, a
 * edição dele vence inteira; se não mexeu, vem o par do servidor.
 */
export function rebaseDestination(base: DestinationValues, current: DestinationValues, server: DestinationValues): Rebased {
  const next: Record<string, unknown> = { ...server }
  const conflicts: Array<keyof DestinationValues> = []
  let hadEdits = false
  // O valor que conta de cada unidade: o número escondido pelo modo não entra.
  const pick = (v: DestinationValues, unit: ReadonlyArray<keyof DestinationValues>): unknown[] => {
    if (unit[0] === 'timing_mode') return v.timing_mode === 'offset' ? [v.timing_mode, v.offset_minutes] : [v.timing_mode]
    if (unit[0] === 'max_mode') return v.max_mode === 'limit' ? [v.max_mode, v.max_per_day] : [v.max_mode]
    return unit.map((k) => v[k])
  }
  for (const unit of mergeUnits()) {
    const mine = pick(current, unit)
    // Não editado, ou editado para o que o servidor já tem: vale o servidor.
    if (same(mine, pick(base, unit)) || same(mine, pick(server, unit))) continue
    hadEdits = true
    for (const key of unit) next[key] = current[key]
    if (!same(pick(server, unit), pick(base, unit))) conflicts.push(unit[0] as keyof DestinationValues)
  }
  return { initial: server, current: next as DestinationValues, hadEdits, conflicts }
}

/**
 * Depois do PUT: os campos em que o destino gravado não é o que a tela mostrava
 * (o disjuntor desligou o destino com a aba aberta, o publicador sumiu). As
 * pausas valem pelo conteúdo, não pela ordem: o backend as devolve ordenadas.
 */
export function divergentFields(shown: DestinationValues, saved: DestinationValues): Array<keyof DestinationValues> {
  const patch = toDestinationPatch(saved, shown)
  if (!patch) return []
  const out: Array<keyof DestinationValues> = []
  if ('enabled' in patch) out.push('enabled')
  if ('timing_mode' in patch || 'offset_minutes' in patch) out.push('timing_mode')
  if ('max_per_day' in patch) out.push('max_mode')
  if ('require_safety_approved' in patch) out.push('require_safety_approved')
  for (const key of Object.keys(patch.options ?? {})) out.push(key as keyof DestinationValues)
  const sorted = (list: readonly PauseRange[]) => [...list].sort((a, b) => a.from.localeCompare(b.from))
  if (!same(sorted(shown.pause_windows), sorted(saved.pause_windows))) out.push('pause_windows')
  return out
}

// ---------------------------------------------------------------------------
// Alterações (o "antes:" e o "Sair sem salvar?")
// ---------------------------------------------------------------------------

const yesNo = (v: unknown) => (v ? 'ligado' : 'desligado')

export const DESTINATION_FIELDS: Record<keyof DestinationValues, FieldSpec> = {
  enabled: { tab: 'destinations', label: 'Publicar no Instagram', format: yesNo },
  timing_mode: {
    tab: 'destinations',
    label: 'Quando publicar',
    format: (v) => (v === 'offset' ? 'minutos depois do YouTube' : 'com o YouTube'),
  },
  offset_minutes: { tab: 'destinations', label: 'Minutos depois do YouTube', format: (v) => `${String(v)} min` },
  max_mode: {
    tab: 'destinations',
    label: 'Quantos por dia',
    format: (v) => (v === 'limit' ? 'com limite' : 'todos os do YouTube'),
  },
  max_per_day: { tab: 'destinations', label: 'Máximo por dia', format: (v) => String(v) },
  require_safety_approved: { tab: 'destinations', label: 'Só aprovados pela segurança', format: yesNo },
  caption_cta: {
    tab: 'destinations',
    label: 'Chamada da legenda',
    format: (v) => (typeof v === 'string' && v.trim() ? v.trim() : 'padrão'),
  },
  include_source_credit: { tab: 'destinations', label: 'Crédito da fonte', format: yesNo },
  hashtags_max: { tab: 'destinations', label: 'Hashtags na legenda', format: (v) => String(v) },
  share_to_feed: { tab: 'destinations', label: 'Mostrar também no feed', format: yesNo },
  trial: { tab: 'destinations', label: 'Reel de teste', format: yesNo },
  pause_windows: {
    tab: 'destinations',
    label: 'Pausas',
    format: (v) => {
      const list = Array.isArray(v) ? (v as PauseRange[]) : []
      if (!list.length) return 'nenhuma'
      return list.map((w) => `${localInputLabel(w.from)} → ${localInputLabel(w.until)}`).join(' · ')
    },
  },
}

/** Os campos que não valem no estado atual (o número escondido pelo modo). */
export function inactiveDestinationFields(v: DestinationValues): Array<keyof DestinationValues> {
  const out: Array<keyof DestinationValues> = []
  if (v.timing_mode !== 'offset') out.push('offset_minutes')
  if (v.max_mode !== 'limit') out.push('max_per_day')
  return out
}

/** Se o Salvar precisa gravar o destino (só quando algo da aba mudou). */
export function hasDestinationChanges(changes: readonly Change[]): boolean {
  return changes.some((c) => c.tab === 'destinations')
}

// ---------------------------------------------------------------------------
// Fila de posts
// ---------------------------------------------------------------------------

export const POST_STATUS: Record<DestinationPostStatus, { label: string; variant: ChipVariant }> = {
  queued: { label: 'Na fila', variant: 'default' },
  preparing: { label: 'Preparando', variant: 'info' },
  ready: { label: 'Pronto', variant: 'primary' },
  publishing: { label: 'Publicando', variant: 'info' },
  published: { label: 'Publicado', variant: 'success' },
  skipped: { label: 'Pulado', variant: 'warning' },
  failed: { label: 'Falhou', variant: 'danger' },
  cancelled: { label: 'Cancelado', variant: 'default' },
  missed: { label: 'Perdido', variant: 'warning' },
  unavailable: { label: 'Indisponível', variant: 'default' },
}

/** Espelho de ``actions.CANCELLABLE`` e ``actions.RETRYABLE`` do backend. */
export const CANCELLABLE: readonly DestinationPostStatus[] = ['queued', 'preparing', 'ready']
export const RETRYABLE: readonly DestinationPostStatus[] = ['failed', 'missed', 'cancelled', 'skipped', 'unavailable']
/**
 * ``tick.MAX_DELAY``, como o ``actions.retry`` confere: passou disso do horário,
 * não dá mais para tentar de novo. O tick desconta o tempo parado; o retry, não.
 */
export const MAX_DELAY_HOURS = 3
/** ``tick.MAX_PUBLISH_CALLS``: no máximo uma republicação por post. */
export const MAX_PUBLISH_CALLS = 2

/** Os filtros da fila, com os estados que cada um mostra. */
export const POST_FILTERS = [
  { value: 'all', label: 'Todos', statuses: [] },
  { value: 'pending', label: 'Pendentes', statuses: ['queued', 'preparing', 'ready', 'publishing'] },
  { value: 'stopped', label: 'Pulados e falhas', statuses: ['skipped', 'failed', 'missed', 'cancelled', 'unavailable'] },
  { value: 'published', label: 'Publicados', statuses: ['published'] },
] as const satisfies ReadonlyArray<{ value: string; label: string; statuses: readonly DestinationPostStatus[] }>

export type PostFilter = (typeof POST_FILTERS)[number]['value']

/** Motivos que o front explica melhor que o rótulo cru. */
const LOCAL_REASONS: Record<string, string> = {
  retry: 'Conferindo no perfil se já saiu, antes de reenviar',
}

/** O motivo para exibir: o rótulo do backend, ou o código quando não há rótulo. */
export function postReason(post: Pick<DestinationPost, 'status_reason' | 'status_reason_label'>): string {
  if (!post.status_reason) return ''
  const local = LOCAL_REASONS[post.status_reason]
  if (local) return local
  return post.status_reason_label || post.status_reason
}

export interface PostActions {
  cancel: boolean
  retry: boolean
  /** Houve chamada de envio: tentar de novo confere o perfil antes (reconcilia). */
  reconcilesFirst: boolean
  /** Por que não dá para tentar de novo, quando o estado sugeriria que dá. */
  hint: string | null
}

function isSafetyReason(reason: string): boolean {
  return reason.startsWith('safety_')
}

/**
 * O caminho de volta de um post pulado pela segurança: não é o botão, é o
 * veredito. Aprovado antes do horário (na revisão humana, ou numa nova
 * checagem, pela varredura), o post volta sozinho para a fila. Bloqueado não
 * tem volta.
 */
const SAFETY_HINTS: Record<string, string> = {
  safety_needs_review:
    'Aprove o vídeo na revisão de segurança: com o horário ainda pela frente, o post volta sozinho para a fila.',
  safety_pending: 'A segurança ainda não avaliou o vídeo: aprovado antes do horário, o post volta sozinho para a fila.',
  safety_missing:
    'O vídeo está sem veredito de segurança: uma checagem que o aprove antes do horário devolve o post à fila.',
  safety_error:
    'A checagem de segurança falhou: uma nova checagem que aprove o vídeo antes do horário devolve o post à fila.',
}

/**
 * O que a linha oferece. As mesmas regras de ``actions.cancel``/``retry`` do
 * backend, para não mostrar botão que só devolveria 409 — o backend confere
 * de novo, e o 409 aparece na linha se o estado mudou no meio.
 */
export function postActions(
  post: Pick<DestinationPost, 'status' | 'status_reason' | 'publish_at' | 'publish_attempts'>,
  destinationEnabled: boolean,
  now: Date = new Date(),
): PostActions {
  const cancel = CANCELLABLE.includes(post.status)
  const none: PostActions = { cancel, retry: false, reconcilesFirst: false, hint: null }
  if (!RETRYABLE.includes(post.status)) return none
  const publishAt = new Date(post.publish_at).getTime()
  if (Number.isNaN(publishAt) || now.getTime() > publishAt + MAX_DELAY_HOURS * 3_600_000) return none
  if (post.status === 'skipped' && isSafetyReason(post.status_reason)) {
    const hint = SAFETY_HINTS[post.status_reason]
    return { ...none, hint: hint ?? null }
  }
  if (post.publish_attempts >= MAX_PUBLISH_CALLS) {
    return { ...none, hint: 'Este post já teve uma republicação. Confira no perfil antes de qualquer coisa.' }
  }
  if (!destinationEnabled) return { ...none, hint: 'Ligue o destino (e salve) para tentar de novo.' }
  return { cancel, retry: true, reconcilesFirst: post.publish_attempts > 0, hint: null }
}

/** O erro de uma ação na linha, com o estado em que o post estava quando ela falhou. */
export interface RowError {
  message: string
  status: DestinationPostStatus
}

/**
 * O erro de uma ação, preso ao estado que o post tem **depois** da releitura
 * da fila (a mutação a espera). Preso ao estado do clique, o 409 de "o post
 * mudou de estado" sumiria na hora: a linha já chega com o estado novo.
 */
export function rowErrorAfterRefetch(
  message: string,
  postId: number,
  statusAtClick: DestinationPostStatus,
  page: DestinationPostsPage | null | undefined,
): RowError {
  return { message, status: page?.data.find((p) => p.id === postId)?.status ?? statusAtClick }
}

/**
 * Os erros que ainda valem: o de um post que mudou de estado depois do erro
 * sai sozinho, em vez de ficar preso na linha até a próxima ação.
 */
export function visibleRowErrors(
  errors: Readonly<Record<number, RowError>>,
  posts: ReadonlyArray<Pick<DestinationPost, 'id' | 'status'>>,
): Record<number, string> {
  const out: Record<number, string> = {}
  for (const post of posts) {
    const error = errors[post.id]
    if (error && error.status === post.status) out[post.id] = error.message
  }
  return out
}
